import ImageGalleryModal from '@components/Shared/ImageGalleryModal';
import { InspectionReportDownload } from '@components/Shared/Inspection/InspectionReportDownload';
import { useLocalization } from '@lib/localization';
import SPInspection from '@lib/spproject/SPInspection';
import { type Inspection } from '@lib/spproject/types';
import {
  AccordionDetails,
  AccordionSummary,
  Box, CircularProgress, List,
  Pagination,
  Tooltip,
  Typography
} from '@mui/material';
import type { IInspection } from '@store/api/inspectionEndpoints';
import { Calendar, Images, User } from 'lucide-react';
import { type MouseEvent, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { AngleDownIcon } from 'react-line-awesome';
import {
  Accordion,
  EmptyPOIContainer,
  InfoChip,
  InspectionHeader,
  InspectionInfo,
  SnapshotChip
} from './Inspections.styled';
import InspectionPointItem from './InspectionPointItem';
import InspectionStatusSummary from './InspectionStatusSummary';
import { useInspectionSnapshots } from './useInspectionSnapshots';

interface Props {
  inspection: IInspection;
  isExpanded: boolean;
  onToggleExpand: (inspectionId: string) => void;
  onPoiClick: (inspectionId: string, poi: Inspection.PointOfInterest) => void;
  loadInspection: (inspectionId: string) => Promise<SPInspection>;
  projectId: string;
  projectName: string;
}

const POIS_PER_PAGE = 5;

const InspectionAccordion = memo<Props>(({ inspection, isExpanded, onToggleExpand, onPoiClick, loadInspection, projectId, projectName }) => {
  const { t } = useLocalization();
  const [currentPage, setCurrentPage] = useState(1);
  const [galleryOpen, setGalleryOpen] = useState(false);

  const { snapshots, loading: snapshotsLoading, load: loadSnapshots } = useInspectionSnapshots(
    inspection.info.id,
    loadInspection
  );

  const snapshotCount = useMemo(
    () => (inspection.resources ?? []).filter(SPInspection.isSnapshot).length,
    [inspection.resources]
  );

  const snapshotImages = useMemo(
    () => snapshots.map((snapshot) => ({
      original: snapshot.resourceUrl,
      thumbnail: snapshot.resourceUrl,
      description: snapshot.name,
    })),
    [snapshots]
  );

  const pois = useMemo<Inspection.PointOfInterest[]>(() => {
    return inspection.pointOfInterests ?? [];
  }, [inspection.pointOfInterests]);

  const paginatedPOIs = useMemo(() => {
    const start = (currentPage - 1) * POIS_PER_PAGE;
    return pois.slice(start, start + POIS_PER_PAGE);
  }, [pois, currentPage]);

  const totalPages = useMemo(() => {
    return Math.ceil(pois.length / POIS_PER_PAGE);
  }, [pois.length]);

  const handleToggle = useCallback(() => {
    onToggleExpand(inspection.info.id);
  }, [onToggleExpand, inspection.info.id]);

  const handlePageChange = useCallback((_: unknown, value: number) => {
    setCurrentPage(prev => (prev === value ? prev : value));
  }, []);

  const handleOpenGallery = useCallback((event: MouseEvent) => {
    event.stopPropagation();
    void loadSnapshots().then(() => setGalleryOpen(true));
  }, [loadSnapshots]);

  useEffect(() => {
    if (!isExpanded) setCurrentPage(1);
  }, [isExpanded]);

  return (
    <Accordion expanded={isExpanded} onChange={handleToggle}>
      <Box sx={{ display: 'flex' }}>
        <AccordionSummary expandIcon={<AngleDownIcon />} sx={{ flexGrow: 1 }}>
          <InspectionHeader>
            <InspectionInfo>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {inspection.info.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {inspection.info.id}
              </Typography>
            </InspectionInfo>

            {/* The gap spaces the chips from each other; the margin keeps the last one off
                the expand icon, so neither has to be paid for on the chips themselves. */}
            <Box sx={{
              display: 'flex', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end',
              gap: 1, marginRight: 1.5
            }}>
              <InspectionStatusSummary pois={pois} />

              {snapshotCount > 0 && (
                <Tooltip title={t('Common_Snapshots')}>
                  <SnapshotChip
                    icon={snapshotsLoading ? <CircularProgress size={13} color="inherit" /> : <Images size={14} />}
                    label={snapshotCount}
                    size="small"
                    color="primary"
                    variant="outlined"
                    clickable
                    onClick={handleOpenGallery}
                  />
                </Tooltip>
              )}

              {inspection.author && (
                <Tooltip title={t('Inspection_UploadedBy')}>
                  <InfoChip
                    icon={<User size={14} style={{ marginRight: '1px' }} />}
                    label={inspection.author.name}
                    size="small"
                    variant="outlined"
                  />
                </Tooltip>
              )}
              <Tooltip title={t('Common_DateCreated')}>
                <InfoChip
                  icon={<Calendar size={14} style={{ marginRight: '1px' }} />}
                  label={new Date(inspection.info.dateCreated).toAgoString()}
                  size="small"
                  variant="outlined"
                />
              </Tooltip>
            </Box>
          </InspectionHeader>
        </AccordionSummary>

        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <InspectionReportDownload inspection={inspection} projectId={projectId} projectName={projectName} />
        </Box>
      </Box>

      <AccordionDetails>
        <List sx={{ padding: 0 }}>
          {paginatedPOIs.map((poi, i) => (
            <InspectionPointItem
              key={poi.id}
              poi={poi}
              inspectionId={inspection.info.id}
              onPoiClick={onPoiClick}
              isFirst={i === 0}
            />
          ))}

          {inspection.pointOfInterests?.length === 0 && (
            <EmptyPOIContainer>
              <Typography color="text.secondary">{t('POI_NotFound')}</Typography>
            </EmptyPOIContainer>
          )}
        </List>

        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Pagination count={totalPages} page={currentPage} onChange={handlePageChange} color="primary" />
          </Box>
        )}
      </AccordionDetails>

      <ImageGalleryModal
        open={galleryOpen}
        onClose={() => setGalleryOpen(false)}
        images={snapshotImages}
        startIndex={0}
      />
    </Accordion>
  );
});

export default InspectionAccordion;
